import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { controlPlane } from '../lib/client'
import { rfStore } from '../lib/rfstore'
import type { RogerTone } from '../lib/audio'
import { useI18n, formatar } from '../lib/i18n'

/**
 * Estação: onde o equipamento comprado vira sinal no ar.
 *
 * Até esta tela existir, a loja vendia coisas que não mudavam nada — a estação
 * do control plane era dipolo, RG-58 e 10 m fixos no código.
 *
 * Só aparece o que já é do operador. O servidor valida de novo: um perfil
 * vindo daqui com uma Yagi seria a forma mais barata de pular a economia.
 */

interface Option {
  id: string
  name: string
  detail: string
}

interface Profile {
  rig_id: string
  antenna_id: string
  feedline_id: string
  feedline_m: number
  height_m: number
  heading_deg: number
  swr: number
  connectors: number
  locator: string
  noise_env: string
  power_w: number
  roger_id?: string
  roger_inicio_id?: string
  amplifier_id?: string
  rotator_id?: string
  tower_id?: string
  filter_id?: string
}

interface StationResponse {
  station: Profile
  rigs: Option[] | null
  antennas: Option[] | null
  feedlines: Option[] | null
  amplifiers: Option[] | null
  rotators: Option[] | null
  towers: Option[] | null
  filters: Option[] | null
  max_power_w: number
  max_height_m: number
  noise_envs: { id: string; name: string }[] | null
  rogers: RogerTone[] | null
  qth_cooldown_h: number
  message?: string
}

export function Station({
  refreshKey,
  onSaved,
}: {
  refreshKey?: number
  onSaved?: () => void
}) {
  const { t } = useI18n()
  const [d, setD] = useState<StationResponse | null>(null)
  const [p, setP] = useState<Profile | null>(null)
  const [open, setOpen] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Do store, não de controlPlane.audioEnabled: assim o botão de prévia reage
  // quando a pessoa liga o áudio na aba Rádio, em vez de ficar congelado no
  // valor de quando esta tela montou.
  const audioLigado = useSyncExternalStore(
    rfStore.subscribe,
    () => rfStore.snapshot().mediaState !== null,
  )


  const load = useCallback(() => {
    fetch('/api/station')
      .then((r) => r.json())
      .then((x: StationResponse) => {
        setD(x)
        setP(x.station)
      })
      .catch(() => {})
  }, [])

  useEffect(load, [load, refreshKey])

  const set = <K extends keyof Profile>(k: K, v: Profile[K]) => {
    setP((old) => (old ? { ...old, [k]: v } : old))
    setMsg(null)
    setErr(null)
  }

  const save = async () => {
    if (!p) return
    setBusy(true)
    setErr(null)
    setMsg(null)
    try {
      const r = await fetch('/api/station', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(p),
      })
      const x = (await r.json()) as StationResponse
      if (!r.ok) {
        setErr(x.message ?? t.station.erroSalvar)
        return
      }
      setD(x)
      setP(x.station)
      setMsg(t.station.sucesso)
      onSaved?.()
    } catch {
      setErr(t.station.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  if (!d || !p) return null

  const pick = (
    label: string,
    key: 'rig_id' | 'antenna_id' | 'feedline_id',
    options: Option[] | null,
  ) => {
    const list = options ?? []
    const chosen = list.find((o) => o.id === p[key])
    return (
      <div className="st-field">
        <label htmlFor={key}>{label}</label>
        <select id={key} value={p[key]} onChange={(e) => set(key, e.target.value)}>
          {list.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        {/* A descrição vem do mesmo motor de RF da loja: é o que o operador
            está de fato colocando no ar. */}
        {chosen?.detail && <small>{chosen.detail}</small>}
      </div>
    )
  }

  // Equipamento OPCIONAL (amplificador, rotor, torre, filtro): "Nenhum" é uma
  // escolha válida, diferente de rig/antena/cabo que são obrigatórios. Só
  // aparece se o operador já tem pelo menos um item da categoria — categoria
  // vazia não pesa a tela com um seletor que só oferece "Nenhum".
  const pickOptional = (
    label: string,
    key: 'amplifier_id' | 'rotator_id' | 'tower_id' | 'filter_id',
    options: Option[] | null,
    hint?: string,
  ) => {
    const list = options ?? []
    if (list.length === 0) return null
    const atual = p[key] || ''
    const chosen = list.find((o) => o.id === atual)
    return (
      <div className="st-field">
        <label htmlFor={key}>{label}</label>
        <select id={key} value={atual} onChange={(e) => set(key, e.target.value)}>
          <option value="">{t.station.nenhum}</option>
          {list.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        {chosen?.detail && <small>{chosen.detail}</small>}
        {!chosen && hint && <small>{hint}</small>}
      </div>
    )
  }

  // Bipe de PTT. Mora AQUI, junto do resto do equipamento, e não em cartão
  // separado: ele é acessório de estação, e espalhar configuração por vários
  // cartões faz a pessoa procurar em três lugares o que devia estar em um.
  //
  // Fica no TOPO do painel, não no fim: a primeira versão o enterrou a 1122 px
  // de rolagem, depois da latitude, e ninguém achou.
  const bipe = (
    label: string,
    key: 'roger_id' | 'roger_inicio_id',
    hint: string,
  ) => {
    const tons = d.rogers ?? []
    const atual = p[key] || 'nenhum'
    const tom = tons.find((tone) => tone.id === atual) ?? null
    return (
      <div className="st-field">
        <label htmlFor={key}>{label}</label>
        <div className="st-bipe">
          <select id={key} value={atual} onChange={(e) => set(key, e.target.value)}>
            {tons.map((tone) => (
              <option key={tone.id} value={tone.id}>
                {tone.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!tom?.steps?.length || !audioLigado}
            onClick={() => void controlPlane.previewTone(tom)}
          >
            {t.station.ouvir}
          </button>
        </div>
        {!audioLigado && tom?.steps?.length ? (
          <small>{t.station.ligueAudioPrevia}</small>
        ) : null}
        <small>{hint}</small>
        {tom?.note && <small>{tom.note}</small>}
        {tom?.etiqueta && <small className="st-etiqueta">{tom.etiqueta}</small>}
      </div>
    )
  }

  const num = (
    label: string,
    key: 'height_m' | 'feedline_m' | 'power_w' | 'swr' | 'heading_deg',
    step: string,
    hint?: string,
  ) => (
    <div className="st-field">
      <label htmlFor={key}>{label}</label>
      <input
        id={key}
        type="number"
        step={step}
        value={p[key]}
        onChange={(e) => set(key, Number(e.target.value))}
      />
      {hint && <small>{hint}</small>}
    </div>
  )

  return (
    <section className="station">
      <button className="st-toggle" onClick={() => setOpen((v) => !v)}>
        {t.station.minhaEstacao}
        <small>
          {(d.antennas ?? []).find((a) => a.id === p.antenna_id)?.name ?? p.antenna_id}
          {' · '}
          {p.power_w} W
        </small>
      </button>

      {open && (
        <>
          {bipe(t.station.bipeRoger, 'roger_id', t.station.bipeRogerHint)}
          {bipe(t.station.bipeInicio, 'roger_inicio_id', t.station.bipeInicioHint)}
          <p className="st-hint">{t.station.bipeHint}</p>

          {pick(t.station.transceptor, 'rig_id', d.rigs)}
          {pick(t.station.antena, 'antenna_id', d.antennas)}
          {pickOptional(t.station.rotor, 'rotator_id', d.rotators, t.station.rotorHint)}
          {(d.antennas ?? []).find((a) => a.id === p.antenna_id)?.detail
            .includes('precisa de rotor') && !p.rotator_id && (
            <p className="denial">{t.station.precisaRotor}</p>
          )}
          {pick(t.station.cabo, 'feedline_id', d.feedlines)}
          {pickOptional(t.station.filtroFI, 'filter_id', d.filters, t.station.filtroHint)}

          {num(t.station.alturaAntena, 'height_m', '1',
            formatar(t.station.alturaHint, { max: d.max_height_m }))}
          {pickOptional(t.station.torre, 'tower_id', d.towers, t.station.torreHint)}
          {num(t.station.direcaoAntena, 'heading_deg', '1', t.station.direcaoHint)}
          {num(t.station.comprimentoCabo, 'feedline_m', '1', t.station.caboHint)}
          {num(t.station.potencia, 'power_w', '1', formatar(t.station.potenciaHint, { max: d.max_power_w }))}
          {pickOptional(t.station.amplificador, 'amplifier_id', d.amplifiers, t.station.amplificadorHint)}
          {num(t.station.swr, 'swr', '0.1', t.station.swrHint)}

          <div className="st-field">
            <label htmlFor="noise_env">{t.station.ambienteQTH}</label>
            <select
              id="noise_env"
              value={p.noise_env}
              onChange={(e) => set('noise_env', e.target.value)}
            >
              {(d.noise_envs ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            <small>{t.station.ambienteHint}</small>
          </div>

          <div className="st-field">
            <label htmlFor="locator">{t.station.locator}</label>
            <input
              id="locator"
              value={p.locator}
              onChange={(e) => set('locator', e.target.value.toUpperCase())}
              placeholder="GG66oe"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={6}
            />
            <small>{t.station.locatorHint}</small>
          </div>
          <p className="st-hint">{formatar(t.station.qthHint, { h: d.qth_cooldown_h })}</p>


          {err && <p className="denial">{err}</p>}
          {msg && <p className="st-ok">{msg}</p>}

          <button className="st-save" onClick={() => void save()} disabled={busy}>
            {busy ? t.station.salvando : t.station.salvarEstacao}
          </button>
        </>
      )}
    </section>
  )
}
